import { FC, useRef, useState } from 'react';
import { useCurrentUser } from '@hooks/users';
import { useUploadAvatarMutation } from '@teachur/http-client';
import { toast } from 'react-toastify';
import { Button } from '@new/common/ui';
import { IconButton } from '@new/common/ui/IconButton/IconButton';
import { Loader } from '@new/common/ui/Loader/Loader';
import { SectionHeading } from '../SectionHeading';
import { PersonalInfoForm } from './PersonalInfoForm';
import { CircleUserRound, Pencil, X } from 'lucide-react';
import { validateAvatarFile } from '../../lib/helpers';

export const PersonalInfoSection: FC = () => {
  const { data: user } = useCurrentUser();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const { mutate: uploadAvatar } = useUploadAvatarMutation({
    onSuccess: () => toast.success('Avatar updated successfully.'),
    onError: () => toast.error('Failed to update avatar.'),
  });

  if (!user) return <Loader />;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <SectionHeading>Personal Info</SectionHeading>
        {isEditMode ? (
          <IconButton
            icon={<X className="w-5 h-5" />}
            label="Cancel edit"
            onClick={() => setIsEditMode(false)}
          />
        ) : (
          <IconButton
            icon={<Pencil className="w-5 h-5 text-white" />}
            label="Edit personal info"
            onClick={() => setIsEditMode(true)}
            className="bg-(--primary-O1100) hover:bg-(--primary-O1000) rounded-md w-10 h-10 flex items-center justify-center"
          />
        )}
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-8 sm:gap-6">
        <div className="w-31.5 flex justify-center items-center h-31.5 rounded-full overflow-hidden bg-(--neutral-black-NB200)">
          {user.avatar?.url ? (
            <img src={user.avatar.url} alt="Profile" className="w-full h-full object-cover" />
          ) : (
            <CircleUserRound size={80} strokeWidth={1} className="text-(--neutral-black-NB400)" />
          )}
        </div>
        <div className="flex flex-col gap-2 w-full sm:w-74.75!">
          <div className="flex gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];

                if (!file) return;

                const errorMessage = await validateAvatarFile(file);

                if (errorMessage) {
                  toast.error(errorMessage);
                  e.target.value = '';
                  return;
                }

                uploadAvatar(file);
                e.target.value = '';
              }}
            />
            <Button
              variant="outline"
              size="sm"
              className="h-9! w-auto! px-4! bg-(--neutral-white-NW1000)! text-(--primary-O900)! border-2 border-(--primary-O900)! rounded-md!"
              onClick={() => fileInputRef.current?.click()}
            >
              Upload photo
            </Button>
          </div>
          <p className="[font-family:var(--font-montserrat)] [font-size:var(--fs-l4)]! text-(--neutral-black-NB600) m-0!">
            Image must be at least 800 x 800px. JPEG or PNG is allowed.
          </p>
        </div>
      </div>

      {isEditMode ? (
        <PersonalInfoForm user={user} onCancel={() => setIsEditMode(false)} />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <p className="[font-family:var(--font-montserrat)] [font-size:var(--fs-l3)]! text-(--neutral-black-NB600) m-0! mb-1">
                First Name
              </p>
              <p className="[font-family:var(--font-montserrat)] [font-size:var(--fs-p2)]! text-(--neutral-black-NB900) m-0!">
                {user.firstName || '-'}
              </p>
            </div>
            <div className="flex-1">
              <p className="[font-family:var(--font-montserrat)] [font-size:var(--fs-l3)]! text-(--neutral-black-NB600) m-0! mb-1">
                Last Name
              </p>
              <p className="[font-family:var(--font-montserrat)] [font-size:var(--fs-p2)]! text-(--neutral-black-NB900) m-0!">
                {user.lastName || '-'}
              </p>
            </div>
          </div>
          <div>
            <p className="[font-family:var(--font-montserrat)] [font-size:var(--fs-l3)]! text-(--neutral-black-NB600) m-0! mb-1">
              Phone Number
            </p>
            <p className="[font-family:var(--font-montserrat)] [font-size:var(--fs-p2)]! text-(--neutral-black-NB900) m-0!">
              {user.phone || '-'}
            </p>
          </div>
          <div>
            <p className="[font-family:var(--font-montserrat)] [font-size:var(--fs-l3)]! text-(--neutral-black-NB600) m-0! mb-1">
              Address
            </p>
            <p className="[font-family:var(--font-montserrat)] [font-size:var(--fs-p2)]! text-(--neutral-black-NB900) m-0!">
              {user.address || '-'}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <p className="[font-family:var(--font-montserrat)] [font-size:var(--fs-l3)]! text-(--neutral-black-NB600) m-0! mb-1">
                City
              </p>
              <p className="[font-family:var(--font-montserrat)] [font-size:var(--fs-p2)]! text-(--neutral-black-NB900) m-0!">
                {user.city || '-'}
              </p>
            </div>
            <div className="flex-1">
              <p className="[font-family:var(--font-montserrat)] [font-size:var(--fs-l3)]! text-(--neutral-black-NB600) m-0! mb-1">
                State/Region
              </p>
              <p className="[font-family:var(--font-montserrat)] [font-size:var(--fs-p2)]! text-(--neutral-black-NB900) m-0!">
                {user.state || '-'}
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <p className="[font-family:var(--font-montserrat)] [font-size:var(--fs-l3)]! text-(--neutral-black-NB600) m-0! mb-1">
                Zip Code
              </p>
              <p className="[font-family:var(--font-montserrat)] [font-size:var(--fs-p2)]! text-(--neutral-black-NB900) m-0!">
                {user.zip || '-'}
              </p>
            </div>
            <div className="flex-1">
              <p className="[font-family:var(--font-montserrat)] [font-size:var(--fs-l3)]! text-(--neutral-black-NB600) m-0! mb-1">
                Country
              </p>
              <p className="[font-family:var(--font-montserrat)] [font-size:var(--fs-p2)]! text-(--neutral-black-NB900) m-0!">
                {user.country || '-'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
